import { useMemo } from 'react'
import { useSpaceSidebar } from '../context/SpaceSidebarDataContext'
import Comment from '../../comments/comment'
import { Skeleton } from '../../../components/ui/skeleton'
import { ScrollArea } from '../../../components/ui/scroll-area'
import {
  EmptyState,
  EmptyStateAction,
  EmptyStateDescription,
  EmptyStateHeader,
  EmptyStateTitle,
} from '../../../components/ui'
import { IonIcon } from '../../../Icons/IonIcon'

const CommentsTab = () => {
  const {
    comments: { data: comments, isLoading, error },
    whitelistedUsers,
    space,
    onOpenBlock,
    onOpenSpace,
  } = useSpaceSidebar()

  // Map comments to their corresponding whitelisted users
  const commentsWithUsers = useMemo(() => {
    if (!comments || !whitelistedUsers) return []

    return comments.map((comment) => {
      const whitelistedUser = comment.whitelistedUserId
        ? whitelistedUsers.find((user) => user.id === comment.whitelistedUserId)
        : null

      return {
        comment,
        whitelistedUser,
      }
    })
  }, [comments, whitelistedUsers])

  if (isLoading) {
    return (
      <ScrollArea className='h-full bg-grey-s1'>
        <div className='p-5 gap-3 flex-col flex'>
          {[...Array(5)].map((_, index) => (
            <div key={index} className='flex flex-col w-full group'>
              {/* Title section skeleton */}
              <div className='flex w-full px-5 py-[6px] rounded-t-md border border-b border-grey-s2 bg-grey-s1'>
                <Skeleton className='h-4 w-48' />
              </div>
              {/* Content section skeleton */}
              <div className='px-5 py-3 bg-white flex flex-col gap-3 rounded-b-md border-x border-b'>
                <div className='flex justify-between gap-2'>
                  <div className='flex gap-2 items-center'>
                    <Skeleton className='w-8 h-8 rounded-full' />
                    <div className='flex items-center gap-2'>
                      <Skeleton className='h-4 w-32' />
                      <Skeleton className='h-4 w-24' />
                    </div>
                  </div>
                  <Skeleton className='w-6 h-6' />
                </div>
                <div className='space-y-2'>
                  <Skeleton className='h-4 w-full' />
                  <Skeleton className='h-4 w-3/4' />
                </div>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    )
  }

  if (error) {
    return (
      <ScrollArea className='h-full'>
        <div className='p-4 flex items-center justify-center'>
          <div className='text-red-500'>
            <p>Error loading comments</p>
          </div>
        </div>
      </ScrollArea>
    )
  }

  if (commentsWithUsers.length === 0) {
    return (
      <ScrollArea className='flex w-full h-full justify-center align-middle p-5'>
        <EmptyState
          className='p-4 gap-4 '
          iconClassName='text-blue-s3'
          icon='chatbubbles'
          iconSize='small'
        >
          <EmptyStateHeader>
            <EmptyStateTitle>No comments found for this space</EmptyStateTitle>
            <EmptyStateDescription>Be the first to comment on this space.</EmptyStateDescription>
          </EmptyStateHeader>
          <EmptyStateAction onClick={onOpenSpace}>
            Go to space
            <IonIcon name='arrow-forward' />
          </EmptyStateAction>
        </EmptyState>
      </ScrollArea>
    )
  }

  return (
    <ScrollArea className='h-full bg-grey-s1'>
      <div className='p-5 gap-3 flex-col flex'>
        {commentsWithUsers.map(({ comment, whitelistedUser }) => (
          <Comment
            key={comment.id}
            comment={comment}
            whitelistedUser={whitelistedUser}
            onOpenBlock={() => {
              if (!space) return
              onOpenBlock({
                spaceId: space.rootNodeId,
                blockId: comment?.block?.id ?? undefined,
                pageId: comment?.page?.id ?? undefined,
              })
            }}
          />
        ))}
      </div>
    </ScrollArea>
  )
}

export default CommentsTab
